/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Dialog } from "primereact";
import ButtonComponent from "../../../../components/button";
import Icons from "../../../../components/icons-helper";
import { theme } from "../../../../styles/theme";
import { CityProps, StateProps } from "../../../../../domain/models/address.model";
import { makeRemoteAddresss } from "../../../../../main/factories/usecases/address-controller/remote-address-controller-factory";
import TooltipComp from "../../../../components/tooltip-info-field";

// Tipos
export type EconomicActivityAction = "create" | "update" | "delete" | "unchanged";

export interface EconomicActivityItem {
  id?: number; // ID existe apenas para itens que vieram do backend
  tempId: string; // ID temporário para controle local
  code: string;
  tributationCode: string;
  description: string;
  aliquot: string;
  stateId?: number;
  cityId?: number;
  stateName?: string;
  cityName?: string;
  action: EconomicActivityAction;
  isEdited: boolean;
}

export interface EconomicActivitiesData {
  toCreate: EconomicActivityItem[];
  toUpdate: EconomicActivityItem[];
  toDelete: number[]; // IDs das atividades a deletar
  isValid: boolean; // Indica se todas as atividades estão válidas
  validationErrors: string[]; // Lista de erros de validação
}

interface EconomicActivitiesFieldsProps {
  initialActivities?: any[]; // Atividades vindas do backend
  onChange?: (data: EconomicActivitiesData) => void;
  edit?: boolean;
}

// Styled Components
const Container = styled.div`
  width: 100%;
  padding: 1rem 0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Table = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 0.8fr 2fr 0.8fr 1fr 1fr 0.8fr;
  gap: 10px;
  padding: 12px 0;
  border-radius: 4px;
  font-weight: 600;
  color: ${theme.colors.darkGray};
  margin-bottom: 10px;
`;

const TableRow = styled.div<{ isDeleted?: boolean }>`
  display: grid;
  grid-template-columns: 0.8fr 0.8fr 2fr 0.8fr 1fr 1fr 0.8fr;
  gap: 8px;
  padding: 12px 0;
  border-radius: 4px;
  margin-bottom: 0px;
  align-items: center;
  opacity: ${(props) => (props.isDeleted ? 0.6 : 1)};
  transition: all 0.2s ease;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
`;

const IconButton = styled.button<{ variant?: "delete" | "undo" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: ${(props) => {
    if (props.variant === "delete") return "#fee";
    if (props.variant === "undo") return "#e3f2fd";
    return theme.colors.lightGray;
  }};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => {
    if (props.variant === "delete") return "#fcc";
    if (props.variant === "undo") return "#bbdefb";
    return "#ddd";
  }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ variant: "new" | "edited" | "deleted" }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  background: ${(props) => {
    if (props.variant === "new") return "#4caf50";
    if (props.variant === "edited") return "#ff9800";
    return "#f44336";
  }};
  color: white;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${theme.colors.darkGray};
`;

const ValidationErrors = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const ValidationTitle = styled.div`
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ValidationList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #856404;
`;

const ValidationItem = styled.li`
  margin: 4px 0;
`;

const ReadOnlyText = styled.div`
  padding: 12px;
  color: ${theme.colors.white};
  font-size: 14px;
`;

const ModalFormGroup = styled.div`
  margin-bottom: 20px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${theme.colors.white};
  font-weight: 500;
`;

const ModalInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${(props) =>
    props.hasError ? "#f44336" : theme.colors.lightGray || "#ddd"};
  border-radius: 1rem;
  transition: border-color 0.2s;
  background-color: transparent;
  color: white;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ModalSelect = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${(props) =>
    props.hasError ? "#f44336" : theme.colors.lightGray || "#ddd"};
  border-radius: 1rem;
  transition: border-color 0.2s;
  background-color: ${theme.colors.darkBlue};
  color: white;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  option {
    background-color: ${theme.colors.darkBlue};
    color: white;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;


export const EconomicActivitiesFields: React.FC<
  EconomicActivitiesFieldsProps
> = ({ initialActivities = [], onChange, edit }) => {
  const [activities, setActivities] = useState<EconomicActivityItem[]>([]);
  const [validationData, setValidationData] = useState<EconomicActivitiesData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<EconomicActivityItem | null>(null);
  const [stateList, setStatesList] = useState<StateProps[]>([]);
  const [citiesList, setCitiesList] = useState<CityProps[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});

  const isSmallScreen = window.innerWidth < 768;

  // Load states on mount
  useEffect(() => {
    (async () => {
      const statesData = await makeRemoteAddresss().loadStates();
      setStatesList(statesData);
    })();
  }, []);

  // Load cities when state changes in the modal
  const loadCities = async (stateId: number): Promise<void> => {
    if (typeof stateId !== "number") return;
    const citiesData = await makeRemoteAddresss().loadCities(stateId);
    setCitiesList(citiesData);
  };

  // Gera os dados no formato necessário para o submit
  const getActivitiesData = (): EconomicActivitiesData => {
    const toCreate = activities.filter((a) => a.action === "create");
    const toUpdate = activities.filter((a) => a.action === "update");
    const toDelete = activities
      .filter((a) => a.action === "delete" && a.id)
      .map((a) => a.id as number);

    // Valida apenas as atividades que não estão deletadas
    const activeActivities = activities.filter((a) => a.action !== "delete");
    const validationErrors: string[] = [];
    let isValid = true;

    activeActivities.forEach((activity, index) => {
      const errors: string[] = [];

      if (!activity.code.trim()) {
        errors.push("código");
      }
      if (!activity.description.trim()) {
        errors.push("descrição");
      }
      if (!activity.aliquot.trim()) {
        errors.push("alíquota");
      }
      if (!activity.stateId) {
        errors.push("estado");
      }
      if (!activity.cityId) {
        errors.push("cidade");
      }

      if (errors.length > 0) {
        isValid = false;
        const position = index + 1;
        validationErrors.push(
          `Atividade ${position}: faltam os campos ${errors.join(", ")}`
        );
      }
    });

    return {
      toCreate,
      toUpdate,
      toDelete,
      isValid,
      validationErrors,
    };
  };

  // Inicializa as atividades vindas do backend
  useEffect(() => {
    if (initialActivities && initialActivities.length > 0) {
      const mappedActivities = initialActivities.map((activity) => ({
        id: activity.id,
        tempId: `existing-${activity.id}`,
        code: activity.code || "",
        tributationCode: activity.tributationCode || "",
        description: activity.description || "",
        aliquot: activity.aliquot?.toString() || "",
        stateId: activity.cityPattern?.state?.id,
        cityId: activity.cityPattern?.id,
        stateName: activity.cityPattern?.state?.name || "",
        cityName: activity.cityPattern?.name || "",
        action: "unchanged" as EconomicActivityAction,
        isEdited: false,
      }));
      setActivities(mappedActivities);
    }
  }, [initialActivities]);

  // Notifica o componente pai sobre as mudanças
  useEffect(() => {
    const data = getActivitiesData();
    setValidationData(data);
    if (onChange) {
      onChange(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  // Adiciona uma nova atividade
  const handleAddActivity = (): void => {
    const newActivity: EconomicActivityItem = {
      tempId: `new-${Date.now()}-${Math.random()}`,
      code: "",
      tributationCode: "",
      description: "",
      aliquot: "",
      stateId: undefined,
      cityId: undefined,
      stateName: "",
      cityName: "",
      action: "create",
      isEdited: false,
    };
    setEditingActivity(newActivity);
    setFormErrors({});
    setCitiesList([]);
    setShowModal(true);
  };

  // Abre modal para editar atividade existente
  const handleEditActivity = (activity: EconomicActivityItem): void => {
    setEditingActivity({ ...activity });
    setFormErrors({});
    if (activity.stateId) {
      loadCities(activity.stateId);
    }
    setShowModal(true);
  };

  // Atualiza campo no formulário do modal
  const handleModalFieldChange = (
    field: keyof EconomicActivityItem,
    value: string | number
  ): void => {
    if (!editingActivity) return;

    setEditingActivity((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };

      // Se mudou o estado, limpa a cidade e carrega novas cidades
      if (field === "stateId") {
        updated.cityId = undefined;
        updated.cityName = "";
        const stateId = value as number;
        if (stateId) {
          loadCities(stateId);
          const state = stateList.find((s) => s.id === stateId);
          updated.stateName = state?.name || "";
        }
      }

      // Se mudou a cidade, atualiza o nome
      if (field === "cityId") {
        const cityId = value as number;
        const city = citiesList.find((c) => c.id === cityId);
        updated.cityName = city?.name || "";
      }

      return updated;
    });

    // Limpa erro do campo quando o usuário começa a digitar
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Salva a atividade editada/criada
  const handleSaveActivity = (): void => {
    if (!editingActivity) return;

    // Validação
    const errors: Record<string, boolean> = {};
    const codeValue = editingActivity.code.trim();
    const tributationCodeValue = editingActivity.tributationCode.trim();

    // Valida Código Tributação Nacional (6 dígitos numéricos)
    if (!codeValue || !/^\d{6}$/.test(codeValue)) errors.code = true;

    // Valida Código Tributação Municipal (3 dígitos numéricos)
    if (!tributationCodeValue || !/^\d{3}$/.test(tributationCodeValue)) errors.tributationCode = true;

    if (!editingActivity.description.trim()) errors.description = true;
    if (!editingActivity.aliquot.trim()) errors.aliquot = true;
    if (!editingActivity.stateId) errors.stateId = true;
    if (!editingActivity.cityId) errors.cityId = true;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setActivities((prev) => {
      const existingIndex = prev.findIndex(
        (a) => a.tempId === editingActivity.tempId
      );

      if (existingIndex >= 0) {
        // Editando atividade existente
        return prev.map((activity) => {
          if (activity.tempId === editingActivity.tempId) {
            const updated = { ...editingActivity };
            // Se não é uma atividade nova, marca como editada
            if (activity.action !== "create" && activity.action !== "delete") {
              updated.action = "update";
              updated.isEdited = true;
            }
            return updated;
          }
          return activity;
        });
      }
      return [...prev, editingActivity];
    });

    setShowModal(false);
    setEditingActivity(null);
    setFormErrors({});
  };

  // Marca uma atividade para deleção
  const handleDeleteActivity = (tempId: string): void => {
    setActivities((prev) =>
      prev.map((activity) => {
        if (activity.tempId === tempId) {
          // Se é uma atividade nova, remove completamente
          if (activity.action === "create") {
            return null as any;
          }
          // Se é uma atividade existente, marca para deletar
          return { ...activity, action: "delete" as EconomicActivityAction };
        }
        return activity;
      }).filter(Boolean),
    );
  };

  // Desfaz a deleção de uma atividade
  const handleUndoDelete = (tempId: string): void => {
    setActivities((prev) =>
      prev.map((activity) => {
        if (activity.tempId === tempId) {
          return {
            ...activity,
            action: activity.isEdited
              ? ("update" as EconomicActivityAction)
              : ("unchanged" as EconomicActivityAction),
          };
        }
        return activity;
      }),
    );
  };

  return (
    <Container>
      <Header>
        {edit && (
          <ButtonComponent
            type="button"
            title="Adicionar Atividade"
            color={theme.colors.white}
            backgroundColor={theme.colors.primary}
            onClick={handleAddActivity}
          />
        )}
      </Header>

      {/* Erros de Validação */}
      {validationData && !validationData.isValid && validationData.validationErrors.length > 0 && (
        <ValidationErrors>
          <ValidationTitle>
            Atenção: Existem campos obrigatórios não preenchidos
          </ValidationTitle>
          <ValidationList>
            {validationData.validationErrors.map((error, index) => (
              <ValidationItem key={index}>{error}</ValidationItem>
            ))}
          </ValidationList>
        </ValidationErrors>
      )}

      {activities.length === 0 ? (
        <EmptyState>
          Nenhuma atividade econômica cadastrada. Clique em &quot;Adicionar
          Atividade&quot; para criar uma nova.
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              CT. Nacional
              <TooltipComp message="Código de Tributação Nacional" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              CT. Municipal
              <TooltipComp message="Código de Tributação Municipal" />
            </div>
            <div>Descrição</div>
            <div>Alíquota (%)</div>
            <div>Estado</div>
            <div>Cidade</div>
            <div>Ações</div>
          </TableHeader>

          {activities.map((activity) => {
            const isDeleted = activity.action === "delete";
            const isNew = activity.action === "create";
            const isEdited = activity.action === "update";

            return (
              <TableRow key={activity.tempId} isDeleted={isDeleted}>
                <div>
                  <ReadOnlyText>{activity.code || "-"}</ReadOnlyText>
                </div>
                <div>
                  <ReadOnlyText>{activity.tributationCode || "-"}</ReadOnlyText>
                </div>
                <div>
                  <ReadOnlyText>{activity.description || "-"}</ReadOnlyText>
                </div>
                <div>
                  <ReadOnlyText>{activity.aliquot || "-"}</ReadOnlyText>
                </div>
                <div>
                  <ReadOnlyText>{activity.stateName || "-"}</ReadOnlyText>
                </div>
                <div>
                  <ReadOnlyText>{activity.cityName || "-"}</ReadOnlyText>
                </div>
                <ActionsCell>
                  {isNew && <Badge variant="new">NOVO</Badge>}
                  {isEdited && <Badge variant="edited">EDITADO</Badge>}
                  {isDeleted && <Badge variant="deleted">DELETADO</Badge>}

                  {!isDeleted ? (
                    <>
                      {edit && (
                        <IconButton
                          type="button"
                          onClick={() => handleEditActivity(activity)}
                          title="Editar atividade"
                        >
                          <Icons
                            name="edit"
                            width={16}
                            height={16}
                            color={theme.colors.primary}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        variant="delete"
                        type="button"
                        onClick={() => handleDeleteActivity(activity.tempId)}
                        title="Deletar atividade"
                        disabled={!edit}
                      >
                        <Icons
                          name="trash"
                          width={16}
                          height={16}
                          color="#f44336"
                        />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      variant="undo"
                      type="button"
                      onClick={() => handleUndoDelete(activity.tempId)}
                      title="Desfazer deleção"
                      disabled={!edit}
                    >
                      <Icons
                        name="update"
                        width={16}
                        height={16}
                        color="#000"
                      />
                    </IconButton>
                  )}
                </ActionsCell>
              </TableRow>
            );
          })}
        </Table>
      )}

      {/* Modal de Edição */}
      <Dialog
        header={editingActivity?.id ? "Editar Atividade Econômica" : "Nova Atividade Econômica"}
        visible={showModal}
        style={{ width: isSmallScreen ? "90vw" : "50vw" }}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
        }}
        footer={null}
        onHide={() => {
          setShowModal(false);
          setEditingActivity(null);
          setFormErrors({});
        }}
      >
        {editingActivity && (
          <div>
            <ModalFormGroup>
              <ModalLabel>Código Tributação Nacional *</ModalLabel>
              <ModalInput
                type="text"
                value={editingActivity.code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  handleModalFieldChange("code", value);
                }}
                hasError={formErrors.code}
                placeholder="Ex: 123456"
                maxLength={6}
              />
              {formErrors.code && (
                <div style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  Deve conter exatamente 6 dígitos numéricos
                </div>
              )}
            </ModalFormGroup>
            <ModalFormGroup>
              <ModalLabel>Código Tributação Municipal *</ModalLabel>
              <ModalInput
                type="text"
                value={editingActivity.tributationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 3);
                  handleModalFieldChange("tributationCode", value);
                }}
                hasError={formErrors.tributationCode}
                placeholder="Ex: 123"
                maxLength={3}
              />
              {formErrors.tributationCode && (
                <div style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  Deve conter exatamente 3 dígitos numéricos
                </div>
              )}
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel>Descrição *</ModalLabel>
              <ModalInput
                type="text"
                value={editingActivity.description}
                onChange={(e) => handleModalFieldChange("description", e.target.value)}
                hasError={formErrors.description}
                placeholder="Descrição da atividade"
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel>Alíquota (%) *</ModalLabel>
              <ModalInput
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={editingActivity.aliquot}
                onChange={(e) => handleModalFieldChange("aliquot", e.target.value)}
                hasError={formErrors.aliquot}
                placeholder="0.00"
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel>Estado *</ModalLabel>
              <ModalSelect
                value={editingActivity.stateId || ""}
                onChange={(e) => handleModalFieldChange("stateId", Number(e.target.value))}
                hasError={formErrors.stateId}
              >
                <option value="">Selecione um estado</option>
                {stateList.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </ModalSelect>
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel>Cidade *</ModalLabel>
              <ModalSelect
                value={editingActivity.cityId || ""}
                onChange={(e) => handleModalFieldChange("cityId", Number(e.target.value))}
                hasError={formErrors.cityId}
                disabled={!editingActivity.stateId}
              >
                <option value="">Selecione uma cidade</option>
                {citiesList.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </ModalSelect>
            </ModalFormGroup>

            <ModalButtonGroup>
              <ButtonComponent
                type="button"
                title="Cancelar"
                color={theme.colors.white}
                backgroundColor={theme.colors.darkGray}
                onClick={() => {
                  setShowModal(false);
                  setEditingActivity(null);
                  setFormErrors({});
                }}
              />
              <ButtonComponent
                type="button"
                title="Salvar"
                color={theme.colors.white}
                backgroundColor={theme.colors.primary}
                onClick={handleSaveActivity}
              />
            </ModalButtonGroup>
          </div>
        )}
      </Dialog>
    </Container>
  );
};
